import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoginState } from '../../types/Session';

export type AuthzContext = {
  permissions: string[];
  uiPermissions: string[];
  scopes: { products: number[]; goToMarkets: number[] };
  isAdmin: boolean;
  adminAccess: boolean;
  dataSource?: string | null;
  salesforce?: {
    available?: boolean;
    returned?: boolean;
    accessToken?: string | null;
    access_token?: string | null;
    idUrl?: string | null;
    id_url?: string | null;
    instanceUrl?: string | null;
    instance_url?: string | null;
  } | null;
  /** Whether this user has linked their Webex account (per-user OAuth). Boolean only — no token/pointer. */
  webex?: {
    connected?: boolean;
  } | null;
};

export type AuthProvider = 'salesforce' | 'cisco_sso' | null;

interface UserState {
  signinState: LoginState;

  authProvider: AuthProvider;

  userData: {
    name: string | null;
    email: string | null;
    sfId: string | null;
    isManager: boolean;
    id: number | null;
  };

  manager: {
    name: string | null;
    email: string | null;
    id: number | null;
  };

  sfToken: string | null;
  toolboxToken: string | null;

  navigationViews: string[];

  authz: AuthzContext | null;

  uiModules: string[];
}

const initialState: UserState = {
  signinState: LoginState.LoggedOut,

  authProvider: null,

  userData: {
    name: null,
    email: null,
    sfId: null,
    isManager: false,
    id: null,
  },

  manager: {
    name: null,
    email: null,
    id: null,
  },

  sfToken: null,
  toolboxToken: null,

  navigationViews: [],
  authz: null,
  uiModules: [],
};

const normalizeStrList = (arr?: unknown): string[] => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((v) => (v == null ? '' : String(v)))
    .map((s) => s.trim())
    .filter(Boolean);
};

const normalizeNumList = (arr?: unknown): number[] => {
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => Number(v)).filter((n) => Number.isFinite(n));
};

const hasAnyPermission = (perms: Set<string>, names: string[]) => names.some((n) => perms.has(n));

const deriveUiModulesFromAuthz = (authz: AuthzContext | null): string[] => {
  if (!authz) return [];

  const perms = new Set(authz.permissions || []);
  const products = authz.scopes?.products || [];
  const gtms = authz.scopes?.goToMarkets || [];

  const canEditSomething = Array.from(perms).some(
    (p) => p.endsWith('.update') || p.endsWith('.create') || p.endsWith('.delete'),
  );

  const canEditProduct = perms.has('api.product.update') && products.length > 0;
  const canReadProduct =
    (perms.has('api.product.read') || perms.has('api.product.update')) && products.length > 0;

  const canEditGtm = perms.has('api.go-to-market.update') && gtms.length > 0;
  const canReadGtm =
    (perms.has('api.go-to-market.read') || perms.has('api.go-to-market.update')) &&
    gtms.length > 0;

  const canUseProductEditor =
    authz.isAdmin || perms.has('page.productEditor.view') || canEditProduct || canReadProduct;

  const canUseGtmEditor =
    authz.isAdmin || perms.has('page.gtmEditor.view') || canEditGtm || canReadGtm;

  const modules = new Set<string>();

  if (
    authz.adminAccess ||
    authz.isAdmin ||
    perms.has('page.admin.view') ||
    canEditSomething ||
    canUseProductEditor ||
    canUseGtmEditor
  ) {
    modules.add('admin');
  }

  if (canUseProductEditor) modules.add('productEditor');
  if (canUseGtmEditor) modules.add('gtmEditor');

  if (hasAnyPermission(perms, ['api.customer.read', 'api.customer.update'])) {
    modules.add('customer');
  }

  if (hasAnyPermission(perms, ['api.opportunity.read', 'api.opportunity.update'])) {
    modules.add('opportunity');
  }

  if (perms.has('page.mssp.view')) modules.add('mssp');
  if (perms.has('page.enablement.view')) modules.add('enablement');
  if (perms.has('page.workshops.view')) modules.add('workshops');
  if (perms.has('page.solutions.view')) modules.add('solutions');
  if (perms.has('page.beta')) modules.add('beta');
  if (perms.has('page.approvals.view')) modules.add('approvals');
  if (perms.has('page.leader.view')) modules.add('leader');
  if (perms.has('page.dashboard.view')) modules.add('dashboard');

  if (
    modules.has('customer') ||
    modules.has('opportunity') ||
    modules.has('productEditor') ||
    modules.has('gtmEditor') ||
    modules.has('mssp') ||
    modules.has('enablement') ||
    modules.has('workshops') ||
    modules.has('solutions') ||
    modules.has('leader') ||
    authz.isAdmin
  ) {
    modules.add('dashboard');
  }

  return Array.from(modules);
};

const mergeLegacyViewsAndModules = (legacyViews: string[], uiModules: string[]) => {
  return Array.from(new Set([...legacyViews, ...uiModules]));
};

const resetUserState = (state: UserState) => {
  state.signinState = LoginState.LoggedOut;
  state.authProvider = null;

  state.userData = {
    name: null,
    email: null,
    sfId: null,
    isManager: false,
    id: null,
  };

  state.manager = {
    name: null,
    email: null,
    id: null,
  };

  state.sfToken = null;
  state.toolboxToken = null;

  state.authz = null;
  state.uiModules = [];
  state.navigationViews = [];
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setSigninState(state, action: PayloadAction<UserState['signinState']>) {
      state.signinState = action.payload;
    },

    setAuthProvider(state, action: PayloadAction<AuthProvider>) {
      state.authProvider = action.payload;
    },

    setUserData(
      state,
      action: PayloadAction<{
        name?: string | null;
        email?: string | null;
        sfId?: string | null;
        isManager?: boolean;
        id?: number | null;
        manager?: number | null;
      }>,
    ) {
      const { name, email, sfId, isManager, id, manager } = action.payload;

      state.userData = {
        name: name ?? state.userData.name,
        email: email ?? state.userData.email,
        sfId: sfId ?? state.userData.sfId,
        isManager: isManager ?? state.userData.isManager,
        id: id ?? state.userData.id,
      };

      if (manager !== undefined) {
        state.manager.id = manager ?? null;
      }
    },

    setUserManagerData(
      state,
      action: PayloadAction<{
        name: string | null;
        email: string | null;
        id?: number | null;
      }>,
    ) {
      const { name, email, id } = action.payload;

      state.manager = {
        name: name ?? state.manager.name,
        email: email ?? state.manager.email,
        id: id ?? state.manager.id,
      };
    },

    setSalesforceToken(state, action: PayloadAction<string | null>) {
      state.sfToken = action.payload;
    },

    setToolboxToken(state, action: PayloadAction<string | null>) {
      state.toolboxToken = action.payload;
    },

    setNavigationViews(state, action: PayloadAction<string[]>) {
      const legacyViews = normalizeStrList(action.payload);
      state.navigationViews = mergeLegacyViewsAndModules(legacyViews, state.uiModules);
    },

    setAuthz(state, action: PayloadAction<AuthzContext | null>) {
      const raw = action.payload;

      if (!raw) {
        state.authz = null;
        state.uiModules = [];
        state.navigationViews = [];
        return;
      }

      const normalized: AuthzContext = {
        permissions: normalizeStrList(raw.permissions),
        uiPermissions: normalizeStrList((raw as any).uiPermissions),
        scopes: {
          products: normalizeNumList(raw.scopes?.products),
          goToMarkets: normalizeNumList(raw.scopes?.goToMarkets),
        },
        isAdmin: !!raw.isAdmin,
        adminAccess: !!raw.adminAccess,
        dataSource: raw.dataSource ?? null,
        salesforce: raw.salesforce ?? null,
        webex: raw.webex
          ? { connected: !!raw.webex.connected }
          : null,
      };

      state.authz = normalized;

      const uiModules = deriveUiModulesFromAuthz(normalized);
      state.uiModules = uiModules;

      state.navigationViews = mergeLegacyViewsAndModules([], uiModules);
    },

    setUiModules(state, action: PayloadAction<string[]>) {
      const mods = normalizeStrList(action.payload);
      state.uiModules = mods;
      state.navigationViews = mergeLegacyViewsAndModules([], mods);
    },

    clearUserSession(state) {
      resetUserState(state);
    },

    reset(state) {
      resetUserState(state);
    },
  },
});

export const {
  setSigninState,
  setAuthProvider,
  setUserData,
  setUserManagerData,
  setSalesforceToken,
  setToolboxToken,
  setNavigationViews,
  setAuthz,
  setUiModules,
  clearUserSession,
  reset,
} = userSlice.actions;

export default userSlice.reducer;